import React, { useEffect, useMemo, useState } from "react";
import {
  HiOutlineDocumentText,
  HiOutlineDownload,
  HiOutlineCalendar,
  HiOutlinePhone,
  HiOutlineClock,
  HiOutlineCheck,
  HiOutlinePhoneIncoming,
  HiOutlineThumbUp,
  HiOutlineMinus,
  HiOutlineExclamation,
  HiOutlineCurrencyDollar,
  HiOutlineVolumeOff,
  HiOutlinePhoneMissedCall,
  HiOutlineFilter,
  HiOutlineUser,
  HiOutlineBriefcase,
  HiOutlinePaperAirplane,
  HiOutlineLocationMarker,
  HiOutlineDuplicate,
  HiOutlineXCircle,
  HiOutlineSwitchHorizontal,
  HiOutlineBan,
  HiOutlineInbox,
  HiOutlineChatAlt2,
  HiOutlineStatusOnline,
  HiOutlineHome,
  HiOutlineQuestionMarkCircle,
} from "react-icons/hi";
import {
  isTruthy,
  openErrorNotification,
} from "../../../../../helpers/methods";
import dayjs from "dayjs";
import { getSourceReportDetails } from "./ShowAllService";
import { getTeamList } from "../../../../MetaData/components/MetaList/components/AddTeamMetaList/CreateService";
import { getStage } from "../../../../MetaData/components/MetaList/components/LeadStageData/LeadStageListService";
import notifiers from "../../../../../global/constants/NotificationConstants";
import YhTable, { YhTableColumn } from "../../../../../ui/YhTable";
import YhInput from "../../../../../ui/YhInput";
import YhSelect from "../../../../../ui/YhSelect";
import { YhDateRangePicker } from "../../../../../ui/YhDateTimePicker";
import { applyLicenseKey } from "../../../../../utils/License";
import ReportStageSummary, {
  buildStageStatsFromHeader,
} from "../../../../../Shared/Reports/ReportStageSummary";
import { useSelector } from "react-redux";
import { selectActiveOrganizationId } from "../../../../../redux/authSlice";

/** react-icons IconType returns ReactNode; cast for JSX (same as AllLeads). */
type IconComp = React.ComponentType<{ className?: string; size?: number }>;
const icon = (I: any): IconComp => I as IconComp;

const DocIcon = icon(HiOutlineDocumentText);
const DownloadIcon = icon(HiOutlineDownload);
type StageStyle = { color: string; bg: string; Icon: IconComp };

const STAGE_STYLE_FALLBACK: StageStyle[] = [
  { color: "#EF4444", bg: "#FEE2E2", Icon: icon(HiOutlinePhone) },
  { color: "#F59E0B", bg: "#FEF3C7", Icon: icon(HiOutlineClock) },
  { color: "#10B981", bg: "#D1FAE5", Icon: icon(HiOutlineCheck) },
  { color: "#3B82F6", bg: "#DBEAFE", Icon: icon(HiOutlinePhoneIncoming) },
  { color: "#8B5CF6", bg: "#F9F0F4", Icon: icon(HiOutlineThumbUp) },
  { color: "#9CA3AF", bg: "#F3F4F6", Icon: icon(HiOutlineMinus) },
  { color: "#F97316", bg: "#FFEDD5", Icon: icon(HiOutlineExclamation) },
  { color: "#EAB308", bg: "#FEF9C3", Icon: icon(HiOutlineCurrencyDollar) },
  { color: "#A02448", bg: "#F9F0F4", Icon: icon(HiOutlineVolumeOff) },
  { color: "#2563EB", bg: "#DBEAFE", Icon: icon(HiOutlinePhoneMissedCall) },
  { color: "#059669", bg: "#D1FAE5", Icon: icon(HiOutlineFilter) },
  { color: "#A02448", bg: "#F9F0F4", Icon: icon(HiOutlineUser) },
  { color: "#0EA5E9", bg: "#E0F2FE", Icon: icon(HiOutlineCalendar) },
  { color: "#1D4ED8", bg: "#DBEAFE", Icon: icon(HiOutlineBriefcase) },
  { color: "#16A34A", bg: "#DCFCE7", Icon: icon(HiOutlinePaperAirplane) },
  { color: "#EC4899", bg: "#FCE7F3", Icon: icon(HiOutlineLocationMarker) },
  { color: "#F59E0B", bg: "#FEF3C7", Icon: icon(HiOutlineDuplicate) },
];

const resolveStageStyle = (label: string, idx: number): StageStyle => {
  const n = label.toLowerCase().replace(/_/g, " ");
  if (n.includes("not reachable") || n.includes("unreachable"))
    return { color: "#EF4444", bg: "#FEE2E2", Icon: icon(HiOutlinePhone) };
  if (n.includes("busy"))
    return { color: "#F59E0B", bg: "#FEF3C7", Icon: icon(HiOutlineClock) };
  if (n.includes("incoming"))
    return { color: "#10B981", bg: "#D1FAE5", Icon: icon(HiOutlineCheck) };
  if (n.includes("call back") || n.includes("callback"))
    return { color: "#3B82F6", bg: "#DBEAFE", Icon: icon(HiOutlinePhoneIncoming) };
  if (n.includes("interested") && !n.includes("not"))
    return { color: "#8B5CF6", bg: "#F9F0F4", Icon: icon(HiOutlineThumbUp) };
  if (n === "null" || n.includes("null"))
    return { color: "#9CA3AF", bg: "#F3F4F6", Icon: icon(HiOutlineMinus) };
  if (n.includes("invalid"))
    return { color: "#EF4444", bg: "#FEE2E2", Icon: icon(HiOutlineExclamation) };
  if (n.includes("low budget") || n.includes("budget"))
    return { color: "#EAB308", bg: "#FEF9C3", Icon: icon(HiOutlineCurrencyDollar) };
  if (n.includes("not answered") || n.includes("no answer"))
    return { color: "#A02448", bg: "#F9F0F4", Icon: icon(HiOutlineVolumeOff) };
  if (n.includes("client disconnect") || n.includes("disconnect"))
    return { color: "#2563EB", bg: "#DBEAFE", Icon: icon(HiOutlinePhoneMissedCall) };
  if (n.includes("pipeline"))
    return { color: "#059669", bg: "#D1FAE5", Icon: icon(HiOutlineFilter) };
  if (n.includes("seller"))
    return { color: "#A02448", bg: "#F9F0F4", Icon: icon(HiOutlineUser) };
  if (n.includes("postponed"))
    return { color: "#0EA5E9", bg: "#E0F2FE", Icon: icon(HiOutlineCalendar) };
  if (n.includes("broker"))
    return { color: "#1D4ED8", bg: "#DBEAFE", Icon: icon(HiOutlineBriefcase) };
  if (n.includes("initial"))
    return { color: "#16A34A", bg: "#DCFCE7", Icon: icon(HiOutlinePaperAirplane) };
  if (n.includes("location"))
    return { color: "#EC4899", bg: "#FCE7F3", Icon: icon(HiOutlineLocationMarker) };
  if (n.includes("duplicate"))
    return { color: "#F59E0B", bg: "#FEF3C7", Icon: icon(HiOutlineDuplicate) };
  if (n.includes("not interested"))
    return { color: "#EF4444", bg: "#FEE2E2", Icon: icon(HiOutlineXCircle) };
  if (n.includes("switched"))
    return { color: "#841C39", bg: "#F9F0F4", Icon: icon(HiOutlineSwitchHorizontal) };
  if (n.includes("out of service") || n.includes("ban"))
    return { color: "#64748B", bg: "#F1F5F9", Icon: icon(HiOutlineBan) };
  if (n.includes("patch"))
    return { color: "#14B8A6", bg: "#CCFBF1", Icon: icon(HiOutlineStatusOnline) };
  if (n.includes("wrong"))
    return { color: "#F97316", bg: "#FFEDD5", Icon: icon(HiOutlineExclamation) };
  if (n.includes("visit"))
    return { color: "#0EA5E9", bg: "#E0F2FE", Icon: icon(HiOutlineHome) };
  if (n.includes("req mismatch") || n.includes("mismatch"))
    return { color: "#A855F7", bg: "#F3E8FF", Icon: icon(HiOutlineQuestionMarkCircle) };
  if (n.includes("chat") || n.includes("remark"))
    return { color: "#841C39", bg: "#F9F0F4", Icon: icon(HiOutlineChatAlt2) };
  if (n.includes("inbox") || n.includes("mail"))
    return { color: "#64748B", bg: "#F1F5F9", Icon: icon(HiOutlineInbox) };
  return STAGE_STYLE_FALLBACK[idx % STAGE_STYLE_FALLBACK.length];
};

const SearchSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const ShowAll = (_props: any) => {
  const activeOrganizationId = useSelector(selectActiveOrganizationId) || "";
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState<any>({
    from: null,
    to: null,
  });
  const [search, setSearch] = useState("");
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 25,
    page: 0,
  });
  const [offialDetailState, setOffialDetailState] = useState<any>({});
  const [teamdata, setTeamData] = useState<any[]>([]);
  const [headerData, setHeaderData] = React.useState<any>();
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [metaData, setMetaData] = useState<any>({});

  const [columns, setColumns] = useState<YhTableColumn[]>([
    { field: "source_name", headerName: "Source Name", flex: 1, minWidth: 150 },
  ]);

  useEffect(() => {
    applyLicenseKey();
  }, []);

  useEffect(() => {
    tableDataShowHandler();
  }, [activeOrganizationId]);

  useEffect(() => {
    const fetchColumns = async () => {
      try {
        const resData = await getStage();
        const stageData = resData.data || [];

        if (stageData.length > 0) {
          const dynamicColumns: YhTableColumn[] = stageData.flatMap(
            (stage: any) => {
              if (stage.sub_Stage_name && stage.sub_Stage_name.length > 0) {
                return stage.sub_Stage_name.map((subStage: any) => ({
                  field: subStage.stage_Name,
                  headerName: `${subStage.stage_Name.replace("_", " ")}`,
                  flex: 1,
                  minWidth: 140,
                }));
              }
              return [];
            }
          );

          setColumns((prevColumns) => [
            ...prevColumns.filter(
              (col) =>
                col.field === "source_name" || col.field === "grandTotal"
            ),
            ...dynamicColumns,
          ]);
        }
      } catch (error) {
        console.error("Error fetching columns:", error);
      }
    };

    fetchColumns();
  }, []);

  const fetchRows = async () => {
    try {
      setLoading(true);
      const payload = {
        startDate: selectedDateRange?.from || "",
        endDate: selectedDateRange?.to || "",
        pageNumber: paginationModel.page,
        pageSize: paginationModel.pageSize,
        teamId: offialDetailState?.team_id?.id
          ? offialDetailState.team_id.id
          : "all",
        search: search || "",
      };

      const response = await getSourceReportDetails(payload);
      const reportData = Array.isArray(response?.data) ? response.data : [];
      setHeaderData(response.dataTotalCount);
      const meta = response?.metadata ?? response?.metaData;
      setMetaData(
        Array.isArray(meta) && meta[0]
          ? meta[0]
          : meta && !Array.isArray(meta)
            ? meta
            : {}
      );
      setLoading(false);

      const transformedRows = reportData.map((item: any, index: number) => ({
        ...item,
        id: item._id || item.id || `row-${index}`,
        source_name: item.source_name ?? "",
      }));

      setRows(transformedRows);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching rows:", error);
      setRows([]);
    }
  };

  useEffect(() => {
    fetchRows();
  }, [
    selectedDateRange,
    paginationModel,
    offialDetailState.team_id,
    search,
    columns,
    activeOrganizationId,
  ]);

  useEffect(() => {
    try {
      const savedColumns = localStorage.getItem("sourcecolumns");
      if (savedColumns) {
        const parsed = JSON.parse(savedColumns);
        if (Array.isArray(parsed) && parsed.length) {
          const cleaned: YhTableColumn[] = parsed
            .filter((col: any) => col?.field && col?.headerName)
            .map((col: any) => ({
              field: col.field,
              headerName: col.headerName,
              minWidth: col.minWidth || 140,
              flex: col.flex || 1,
            }));
          if (cleaned.length) setColumns(cleaned);
        }
      }
    } catch {
      localStorage.removeItem("sourcecolumns");
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(
      "sourcecolumns",
      JSON.stringify(
        columns.map((col) => ({
          field: col.field,
          headerName: col.headerName,
          minWidth: col.minWidth || 140,
          flex: col.flex || 1,
        }))
      )
    );
  }, [columns]);

  const tableDataShowHandler = async () => {
    try {
      setLoading(true);
      const response = await getTeamList();
      const teams = (response?.data || []).map((team: any) => ({
        id: team._id,
        label: team.team_name,
      }));
      setTeamData([{ id: "all", label: "All" }, ...teams]);
      setLoading(false);
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
      setLoading(false);
    }
  };

  const handleAutocompleteChange = async (name: string, value: any) => {
    if (!value || value === "") {
      setOffialDetailState((prevFormData: any) => ({
        ...prevFormData,
        [name]: {
          error: "",
          value: "",
          id: null,
        },
      }));
      return;
    }
    if (value?.label === "All" || value?.id === "all") {
      setOffialDetailState((prevFormData: any) => ({
        ...prevFormData,
        [name]: {
          error: "",
          value: "All",
          id: null,
        },
      }));
      return;
    }
    setOffialDetailState((prevFormData: any) => ({
      ...prevFormData,
      [name]: {
        error: "",
        value: value?.label || "",
        id: value?.id,
      },
    }));
  };

  const handleTeamChange = (teamId: string) => {
    if (!teamId || teamId === "all") {
      handleAutocompleteChange("team_id", { id: "all", label: "All" });
      return;
    }
    const opt = teamdata.find((t: any) => String(t.id) === String(teamId));
    handleAutocompleteChange("team_id", opt || null);
  };

  const teamOptions = useMemo(
    () =>
      (teamdata || []).map((t: any) => ({
        label: t.label,
        value: String(t.id),
      })),
    [teamdata]
  );

  const stageStats = useMemo(
    () => buildStageStatsFromHeader(headerData, resolveStageStyle),
    [headerData]
  );

  const exportCsv = () => {
    if (!rows.length) return;
    const headers = columns.map((c) => c.headerName || c.field);
    const fields = columns.map((c) => c.field);
    const lines = [
      headers.join(","),
      ...rows.map((row) =>
        fields
          .map((f) => {
            const v = row[f] ?? "";
            const s = String(v).replace(/"/g, '""');
            return `"${s}"`;
          })
          .join(",")
      ),
    ];
    const blob = new Blob([lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `source-report-${dayjs().format("YYYYMMDD")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-white">
      {/* Page header */}
      <div className="mb-4 flex shrink-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#EEF0FF] text-[#A02448]">
            <DocIcon className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-[#1E1B4B] sm:text-2xl">
              Source Report
            </h1>
            <p className="mt-0.5 text-sm text-[#6B7280]">
              View all Source Reports and lead sources performance.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <button
            type="button"
            onClick={exportCsv}
            className="inline-flex h-10 items-center gap-2 rounded-xl bg-[#EEF0FF] px-3.5 text-sm font-semibold text-[#A02448] transition hover:bg-[#E0E4FF]"
          >
            <DownloadIcon className="h-4 w-4" />
            Export
          </button>
        </div>
      </div>

      <ReportStageSummary
        total={headerData?.total_count}
        stats={stageStats}
        loading={!headerData && loading}
        totalLabel="Leads in selected range"
      />

      {/* Search + filters */}
      <div className="mb-3 flex shrink-0 flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="w-full max-w-[420px]">
          <YhInput
            placeholder="Search by source name..."
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus={isSearchFocused}
            startAdornment={<SearchSvg />}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setIsSearchFocused(false)}
          />
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <span className="text-sm font-medium text-[#6B7280]">Filters</span>

          <YhSelect
            placeholder="Team"
            searchPlaceholder="Search team..."
            showOptionIcon={false}
            className="!w-[200px] shrink-0"
            value={
              offialDetailState?.team_id?.id
                ? String(offialDetailState.team_id.id)
                : "all"
            }
            options={teamOptions}
            onChange={(e) => handleTeamChange(String(e.target.value || ""))}
          />

          <YhDateRangePicker
            className="!w-[240px] shrink-0"
            placeholder="MM/DD/YYYY - MM/DD/YYYY"
            value={{
              from: selectedDateRange?.from || null,
              to: selectedDateRange?.to || null,
            }}
            onChange={(range) => setSelectedDateRange(range)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="min-h-0 flex-1 overflow-hidden rounded-2xl border border-[#EEF0F5] bg-white shadow-[0_4px_20px_rgba(15,23,42,0.04)]">
        <YhTable
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id || row._id}
          rowHeight={56}
          loading={loading}
          emptyMessage="No report rows yet"
          className="h-full min-h-0 !rounded-none !border-0 !shadow-none"
          page={paginationModel.page}
          pageSize={paginationModel.pageSize}
          totalRows={metaData?.total || rows.length || 0}
          onPageChange={(p) =>
            setPaginationModel((prev) => ({ ...prev, page: p }))
          }
          onPageSizeChange={(s) =>
            setPaginationModel({ pageSize: s, page: 0 })
          }
        />
      </div>
    </div>
  );
};

export default ShowAll;
